import React from 'react';
import { Skeleton, cn } from '@strivelabs/ui-core';

export type LoadingSkeletonVariant = 'list' | 'code';

export interface LoadingSkeletonProps {
  /** Visual shape of the skeleton. Defaults to `list`. */
  variant?: LoadingSkeletonVariant;
  /** Row count for the `list` variant. Ignored by other variants. */
  rows?: number;
  /** Render a faux line-number gutter (code variant only). */
  showLineNumbers?: boolean;
  className?: string;
}

const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  variant = 'list',
  rows = 6,
  showLineNumbers = true,
  className,
}) => {
  if (variant === 'code') {
    return <CodeVariant showLineNumbers={showLineNumbers} className={className} />;
  }
  return <ListVariant rows={rows} className={className} />;
};

export default LoadingSkeleton;

/* -------------------------------------------------------------------------- */
/*  list — original card/list-row placeholder                                  */
/* -------------------------------------------------------------------------- */

const ListVariant: React.FC<{ rows: number; className?: string }> = ({ rows, className }) => (
  <div
    className={cn(
      'max-h-[600px] overflow-y-auto rounded-lg border border-border bg-card',
      className,
    )}
  >
    <div className="divide-y divide-border">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="p-4 space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ))}
    </div>
  </div>
);

/* -------------------------------------------------------------------------- */
/*  code — line-shaped bars, optional gutter, fills the container             */
/* -------------------------------------------------------------------------- */

// Deterministic varied-width / indent pattern. Picked to look like real source:
// occasional indents, short and long lines mixed, the odd blank "section break".
const CODE_SKELETON_LINES: Array<{ width: string; indent: string; blank?: boolean }> = [
  { width: 'w-1/3', indent: 'ml-0' },
  { width: 'w-3/4', indent: 'ml-4' },
  { width: 'w-5/6', indent: 'ml-8' },
  { width: 'w-2/3', indent: 'ml-8' },
  { width: 'w-1/2', indent: 'ml-12' },
  { width: 'w-3/5', indent: 'ml-4' },
  { width: 'w-1/4', indent: 'ml-0', blank: true },
  { width: 'w-2/5', indent: 'ml-0' },
  { width: 'w-4/5', indent: 'ml-4' },
  { width: 'w-3/4', indent: 'ml-8' },
  { width: 'w-1/2', indent: 'ml-12' },
  { width: 'w-2/3', indent: 'ml-12' },
  { width: 'w-1/3', indent: 'ml-4' },
  { width: 'w-3/5', indent: 'ml-0', blank: true },
  { width: 'w-5/6', indent: 'ml-0' },
  { width: 'w-2/3', indent: 'ml-4' },
  { width: 'w-1/2', indent: 'ml-8' },
  { width: 'w-3/4', indent: 'ml-8' },
  { width: 'w-2/5', indent: 'ml-4' },
  { width: 'w-1/3', indent: 'ml-0' },
];

// Repeat the pattern enough times to cover any reasonable canvas height
// (~80 lines at 1.5rem each ≈ 1920px). `overflow-hidden` on the outer clips
// anything past the visible area, so over-rendering is cheap and avoids
// measuring the container.
const CODE_PATTERN_REPEAT = 4;

const CodeVariant: React.FC<{ showLineNumbers: boolean; className?: string }> = ({
  showLineNumbers,
  className,
}) => (
  <div
    className={cn(
      'h-full w-full overflow-hidden bg-background animate-pulse',
      className,
    )}
  >
    <div className="flex h-full w-full px-4 py-4">
      {showLineNumbers && (
        <div className="mr-3 w-8 shrink-0 space-y-2 pt-px">
          {Array.from({ length: CODE_SKELETON_LINES.length * CODE_PATTERN_REPEAT }).map((_, i) => (
            <div key={i} className="h-2 w-4 rounded-sm bg-muted" />
          ))}
        </div>
      )}
      <div className="flex-1 space-y-2">
        {Array.from({ length: CODE_PATTERN_REPEAT }).flatMap((_, r) =>
          CODE_SKELETON_LINES.map((line, i) =>
            line.blank ? (
              <div key={`${r}-${i}`} className="h-2" />
            ) : (
              <div
                key={`${r}-${i}`}
                className={cn('h-2 rounded-sm bg-muted', line.indent, line.width)}
              />
            ),
          ),
        )}
      </div>
    </div>
  </div>
);
